import axios from 'axios';
import {
  User,
  LaserMachine,
  Job,
  HMIMessage,
  MachineData,
  File,
  MachineSpecifications,
  CurrentProductionJob,
  ResellerRecord,
} from '../types';
import MockAuthService from './mockAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const toNumber = (v: any, fallback = 0) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : fallback;
};

const normalizeMachineStatus = (status: any): LaserMachine['status'] => {
  const s = String(status || '').trim().toUpperCase();
  switch (s) {
    case 'ONLINE': return 'online';
    case 'OFFLINE': return 'offline';
    case 'MAINTENANCE': return 'maintenance';
    case 'ERROR': return 'error';
    case 'DISABLED': return 'offline';
    default: return 'offline';
  }
};

const normalizeMachineSpecifications = (specs: any): MachineSpecifications => {
  const workArea = specs?.workArea || specs?.workarea || {};
  return {
    power: toNumber(specs?.power, 0),
    workArea: {
      width: toNumber(workArea?.width, 0),
      height: toNumber(workArea?.height, 0),
    },
    maxSpeed: toNumber(specs?.maxSpeed, 0),
    supportedMaterials: Array.isArray(specs?.supportedMaterials) ? specs.supportedMaterials.map((x: any) => String(x)) : [],
  };
};

const extractRustdeskData = (machine: any): Partial<LaserMachine> => {
  const telemetry = Array.isArray(machine?.telemetry) ? machine.telemetry : [];
  for (const entry of telemetry) {
    const rustdesk = entry?.customData?.rustdesk;
    if (!rustdesk || typeof rustdesk !== 'object') {
      continue;
    }

    const id = typeof rustdesk.id === 'string' ? rustdesk.id : '';
    const password = typeof rustdesk.password === 'string' ? rustdesk.password : '';
    const updatedAt = typeof rustdesk.updatedAt === 'string' ? rustdesk.updatedAt : entry?.timestamp ?? null;

    if (id || password) {
      return {
        rustdeskId: id,
        rustdeskPassword: password,
        rustdeskUpdatedAt: updatedAt,
      };
    }
  }

  return {};
};

const normalizeMachine = (m: any): LaserMachine => {
  const lastHeartbeat = m?.lastHeartbeat ?? null;
  const createdAt = m?.createdAt ?? null;
  const lastSeen = lastHeartbeat ?? m?.lastSeen ?? m?.updatedAt ?? createdAt ?? null;
  const currentProduction: CurrentProductionJob | undefined = Array.isArray(m?.nestings) && m.nestings.length
    ? (m.nestings[0] as any)
    : (m?.currentProduction as any);
  const rustdeskData = extractRustdeskData(m);
  return {
    id: String(m?.id || ''),
    name: String(m?.name || 'Macchina'),
    model: String(m?.model || ''),
    serialNumber: String(m?.serialNumber || ''),
    companyId: m?.companyId ?? null,
    ownerId: m?.companyId ?? null, // frontend historically used ownerId; backend uses companyId
    resellerId: m?.resellerId ?? undefined,
    customerId: m?.customerId ?? undefined,
    companyName: m?.company?.name ?? m?.companyName ?? null,
    resellerName: m?.reseller?.name ?? m?.resellerName ?? null,
    company: m?.company ? {
      id: String(m.company.id || ''),
      name: String(m.company.name || ''),
    } : null,
    reseller: m?.reseller ? {
      id: String(m.reseller.id || ''),
      name: String(m.reseller.name || ''),
      email: typeof m.reseller.email === 'string' ? m.reseller.email : undefined,
      phone: typeof m.reseller.phone === 'string' ? m.reseller.phone : null,
      country: typeof m.reseller.country === 'string' ? m.reseller.country : undefined,
    } : null,
    status: normalizeMachineStatus(m?.status),
    lastSeen,
    currentJob: m?.currentJob ?? undefined,
    currentProduction,
    specifications: normalizeMachineSpecifications(m?.specifications),
    location: String(m?.location || ''),
    installationDate: m?.installationDate ?? createdAt ?? null,
    warrantyExpiry: m?.warrantyExpiry ?? undefined,
    softwareVersion: String(m?.softwareVersion || m?.fwVersion || ''),
    firmwareVersion: String(m?.fwVersion || m?.firmwareVersion || ''),
    licenseExpirationDate: m?.licenseExpirationDate ?? null,
    updateServiceEnabled: !!m?.updateServiceEnabled,
    updateServiceExpiresAt: m?.updateServiceExpiresAt ?? null,
    serviceContractEnabled: !!m?.serviceContractEnabled,
    serviceContractExpiresAt: m?.serviceContractExpiresAt ?? null,
    arturoEnabled: !!m?.arturoEnabled,
    arturoExpiresAt: m?.arturoExpiresAt ?? null,

    // Keep backend fields accessible for UI logic (extra props are OK in TS structural typing)
    ...(m?.mode ? { mode: m.mode } : {}),
    ...(lastHeartbeat ? { lastHeartbeat } : {}),
    ...rustdeskData,
  };
};

export const authService = {
  login: async (email: string, password: string) => {
    /* 
    // Check if credentials match a mock user (for demo buttons to work in any env)
    const mockUser = MockAuthService.users.find(u => u.email === email && u.password === password);
    if (mockUser) {
      console.log('Using mock login for demo user:', email);
      return MockAuthService.login(email, password);
    }
    */

    if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_DATA === 'true') {
      return MockAuthService.login(email, password);
    }
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_DATA === 'true') {
      localStorage.removeItem('authToken');
      return;
    }
    await api.post('/auth/logout');
    localStorage.removeItem('authToken');
  },

  getCurrentUser: async (): Promise<User> => {
    if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_DATA === 'true') {
      return MockAuthService.getCurrentUser();
    }
    const response = await api.get('/auth/me');
    return response.data;
  },

  register: async (data: {
    email: string;
    password: string;
    displayName: string;
    companyName: string;
    phone?: string;
    vatNumber?: string;
  }) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  verifyEmail: async (token: string) => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  googleLogin: async (idToken: string, companyName?: string) => {
    const response = await api.post('/auth/google', { idToken, companyName });
    return response.data;
  },

  appleLogin: async (idToken: string, displayName?: string, companyName?: string) => {
    const response = await api.post('/auth/apple', { idToken, displayName, companyName });
    return response.data;
  },
};

// --- Passkey (WebAuthn) ---
export const webauthnService = {
  isSupported: (): boolean =>
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    typeof navigator?.credentials?.create === 'function',

  registerPasskey: async (deviceName?: string) => {
    const { startRegistration } = await import('@simplewebauthn/browser');
    const optRes = await api.post('/auth/webauthn/register/options');
    const { options, challengeId } = optRes.data?.data || {};
    const attResp = await startRegistration({ optionsJSON: options });
    const verifyRes = await api.post('/auth/webauthn/register/verify', {
      response: attResp,
      challengeId,
      deviceName,
    });
    return verifyRes.data?.data;
  },

  loginWithPasskey: async () => {
    const { startAuthentication } = await import('@simplewebauthn/browser');
    const optRes = await api.post('/auth/webauthn/login/options');
    const { options, challengeId } = optRes.data?.data || {};
    const authResp = await startAuthentication({ optionsJSON: options });
    const verifyRes = await api.post('/auth/webauthn/login/verify', {
      response: authResp,
      challengeId,
    });
    return verifyRes.data?.data;
  },

  listCredentials: async (): Promise<Array<{
    id: string;
    deviceName: string | null;
    deviceType: string | null;
    backedUp: boolean;
    createdAt: string;
    lastUsedAt: string | null;
  }>> => {
    const response = await api.get('/auth/webauthn/credentials');
    return response.data?.data?.credentials || [];
  },

  deleteCredential: async (id: string): Promise<void> => {
    await api.delete(`/auth/webauthn/credentials/${id}`);
  },
};

// --- QR cross-device login ---
export const qrLoginService = {
  create: async (): Promise<{ publicId: string; expiresAt: string; ttlSeconds: number }> => {
    const response = await api.post('/auth/qr/create');
    return response.data?.data;
  },
  status: async (publicId: string): Promise<{
    status: 'PENDING' | 'APPROVED' | 'CONSUMED' | 'DENIED' | 'EXPIRED';
    token?: string;
    refreshToken?: string;
    user?: any;
  }> => {
    const response = await api.get(`/auth/qr/${publicId}/status`);
    return response.data?.data;
  },
  info: async (publicId: string): Promise<{
    status: string;
    ipAddress: string | null;
    userAgent: string | null;
    createdAt: string;
    expiresAt: string;
  }> => {
    const response = await api.get(`/auth/qr/${publicId}/info`);
    return response.data?.data;
  },
  approve: async (publicId: string): Promise<void> => {
    await api.post(`/auth/qr/${publicId}/approve`);
  },
  deny: async (publicId: string): Promise<void> => {
    await api.post(`/auth/qr/${publicId}/deny`);
  },
};

export const machineService = {
  getMachines: async (companyId: string): Promise<LaserMachine[]> => {
    const response = await api.get(`/machines?companyId=${companyId}`);
    // API returns { success, data: { machines: [], pagination: {} } }
    const machines = response.data?.data?.machines || [];
    return machines.map(normalizeMachine);
  },

  getMachine: async (id: string): Promise<LaserMachine> => {
    const response = await api.get(`/machines/${id}`);
    return normalizeMachine(response.data?.data?.machine);
  },

  updateMachine: async (id: string, data: Partial<LaserMachine>): Promise<LaserMachine> => {
    const response = await api.put(`/machines/${id}`, data);
    return normalizeMachine(response.data?.data?.machine);
  },

  createMachine: async (data: Partial<LaserMachine>): Promise<LaserMachine> => {
    const response = await api.post('/machines', data);
    return normalizeMachine(response.data?.data?.machine);
  },

  deleteMachine: async (id: string): Promise<void> => {
    await api.delete(`/machines/${id}`);
  },

  sendCommand: async (machineId: string, command: string, parameters?: any) => {
    const response = await api.post(`/machines/${machineId}/commands`, { command, parameters });
    return response.data;
  },

  generateCredentials: async (machineId: string) => {
    const response = await api.post(`/machines/${machineId}/credentials`);
    return response.data;
  },

  generateOfflineLicense: async (machineId: string, data: { expirationDate: Date | null, type: string }) => {
    const response = await api.post(`/machines/${machineId}/offline-license`, data);
    return response.data;
  },
};

export const jobService = {
  getJobs: async (machineId?: string): Promise<Job[]> => {
    const url = machineId ? `/jobs?machineId=${machineId}` : '/jobs';
    const response = await api.get(url);
    return response.data;
  },

  getJob: async (id: string): Promise<Job> => {
    const response = await api.get(`/jobs/${id}`);
    return response.data;
  },

  createJob: async (jobData: Omit<Job, 'id' | 'createdAt' | 'status' | 'progress'>): Promise<Job> => {
    const response = await api.post('/jobs', jobData);
    return response.data;
  },

  updateJob: async (id: string, data: Partial<Job>): Promise<Job> => {
    const response = await api.put(`/jobs/${id}`, data);
    return response.data;
  },

  startJob: async (id: string): Promise<Job> => {
    const response = await api.post(`/jobs/${id}/start`);
    return response.data;
  },

  pauseJob: async (id: string): Promise<Job> => {
    const response = await api.post(`/jobs/${id}/pause`);
    return response.data;
  },

  stopJob: async (id: string): Promise<Job> => {
    const response = await api.post(`/jobs/${id}/stop`);
    return response.data;
  },
};

export const companyService = {
  getCompanies: async (): Promise<any[]> => {
    const response = await api.get('/companies');
    // API returns { success, data: { companies: [] } }
    return response.data?.data?.companies || [];
  },

  createCompany: async (companyData: any): Promise<any> => {
    const response = await api.post('/companies', companyData);
    return response.data?.data?.company;
  },

  updateCompany: async (id: string, data: any): Promise<any> => {
    const response = await api.put(`/companies/${id}`, data);
    return response.data?.data?.company;
  },

  deleteCompany: async (id: string): Promise<void> => {
    await api.delete(`/companies/${id}`);
  },

  getUnassigned: async (): Promise<any[]> => {
    const response = await api.get('/companies/unassigned');
    return response.data?.data?.companies || [];
  },

  assignReseller: async (companyId: string, resellerId: string): Promise<any> => {
    const response = await api.put(`/companies/${companyId}/assign-reseller`, { resellerId });
    return response.data?.data?.company;
  },
};

export const resellerService = {
  getResellers: async (): Promise<ResellerRecord[]> => {
    const response = await api.get('/resellers');
    return response.data?.data?.resellers || [];
  },

  getReseller: async (id: string): Promise<ResellerRecord> => {
    const response = await api.get(`/resellers/${id}`);
    return response.data?.data?.reseller;
  },

  resetPassword: async (id: string): Promise<{ temporaryPassword: string }> => {
    const response = await api.post(`/resellers/${id}/reset-password`);
    return {
      temporaryPassword: response.data?.data?.temporaryPassword || '',
    };
  },

  deleteReseller: async (id: string): Promise<void> => {
    await api.delete(`/resellers/${id}`);
  },
};

export const activationService = {
  getTokens: async (status?: string): Promise<any[]> => {
    const url = status ? `/activation/tokens?status=${status}` : '/activation/tokens';
    const response = await api.get(url);
    return response.data?.data?.tokens || [];
  },
  generateTokens: async (count: number): Promise<any[]> => {
    const response = await api.post('/activation/generate', { count });
    return response.data?.data?.tokens || [];
  },
  deleteToken: async (id: string): Promise<void> => {
    await api.delete(`/activation/tokens/${id}`);
  }
};

export type SoftwareRelease = {
  id: string; product: string; version: string; fileName: string; fileSize: number;
  sha256: string; notes: string | null; isActive: boolean; createdAt: string;
};
export const softwareUpdatesService = {
  getReleases: async (product: string, licenseId?: string) => {
    const response = await api.get('/software-updates/releases', { params: { product, ...(licenseId ? { licenseId } : {}) } });
    return response.data.data as { releases: SoftwareRelease[]; latestReleaseId: string | null; maxUploadBytes: number };
  },
  uploadRelease: async (product: string, version: string, notes: string, file: globalThis.File) => {
    const params = new URLSearchParams({ product, version, notes });
    await api.post(`/software-updates/releases?${params}`, file, {
      headers: { 'Content-Type': 'application/octet-stream', 'X-File-Name': file.name },
      timeout: 300000, transformRequest: [(data) => data],
    });
  },
  setActive: async (id: string, isActive: boolean) => {
    await api.patch(`/software-updates/releases/${id}`, { isActive });
  },
  deleteRelease: async (id: string) => { await api.delete(`/software-updates/releases/${id}`); },
  downloadRelease: (id: string, licenseId?: string) => api.get(`/software-updates/releases/${id}/download`, {
    params: licenseId ? { licenseId } : {}, responseType: 'blob', timeout: 300000,
  }),
};

export const hmiUpdatesService = {
  getReleases: async (): Promise<{ releases: any[]; latestReleaseId: string | null }> => {
    const response = await api.get('/hmi-updates/releases');
    return {
      releases: response.data?.data?.releases || [],
      latestReleaseId: response.data?.data?.latestReleaseId || null,
    };
  },
  uploadRelease: async (payload: { version: string; notes?: string; file: any }): Promise<any> => {
    const version = String(payload.version || '').trim();
    const params = new URLSearchParams({ version });
    if (payload.notes?.trim()) {
      params.set('notes', payload.notes.trim());
    }

    const response = await api.post(`/hmi-updates/releases?${params.toString()}`, payload.file, {
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-File-Name': payload.file?.name || 'HMI_Setup.exe',
      },
      timeout: 300000,
      transformRequest: [(data) => data],
    });

    return response.data?.data?.release;
  },
  downloadRelease: async (id: string): Promise<any> => {
    const response = await api.get(`/hmi-updates/releases/${id}/download`, {
      responseType: 'blob',
      timeout: 300000,
    });
    return response;
  },
  createShareLink: async (id: string, expiresHours = 24): Promise<{ url: string; path: string; expiresHours: number }> => {
    const response = await api.post(`/hmi-updates/releases/${id}/share-link`, { expiresHours });
    return response.data?.data;
  },
  deleteRelease: async (id: string): Promise<void> => {
    await api.delete(`/hmi-updates/releases/${id}`);
  },
};

export type DatabaseTableStat = {
  name: string;
  count: number;
  sizeBytes: number;
  size: string;
  status: 'healthy' | 'warning' | 'error';
};

export type DatabaseBackupItem = {
  fileName: string;
  sizeBytes: number;
  size: string;
  createdAt: string;
  modifiedAt?: string;
};

export type DatabaseStatsResponse = {
  database: {
    name: string;
    sizeBytes: number;
    size: string;
    totalTables: number;
    totalRecords: number;
    maintenanceInProgress: string | null;
  };
  tables: DatabaseTableStat[];
  backupSummary: {
    count: number;
    latestBackup: DatabaseBackupItem | null;
  };
  updatedAt: string;
};

export const databaseService = {
  getStats: async (): Promise<DatabaseStatsResponse> => {
    const response = await api.get('/admin/database/stats');
    return response.data?.data;
  },
  listBackups: async (): Promise<DatabaseBackupItem[]> => {
    const response = await api.get('/admin/database/backups');
    return response.data?.data?.backups || [];
  },
  createBackup: async (label?: string): Promise<DatabaseBackupItem> => {
    const response = await api.post('/admin/database/backup', { label: label?.trim() || undefined });
    return response.data?.data?.backup;
  },
  downloadBackup: async (fileName: string) => {
    return api.get(`/admin/database/backups/${encodeURIComponent(fileName)}/download`, {
      responseType: 'blob',
      timeout: 300000,
    });
  },
  restoreBackup: async (fileName: string): Promise<{ restored: boolean; fileName: string; restoredAt: string }> => {
    const response = await api.post('/admin/database/restore', { fileName });
    return response.data?.data;
  },
  cleanupBackups: async (retentionDays?: number): Promise<{
    retentionDays: number;
    removedCount: number;
    removedBackups: DatabaseBackupItem[];
  }> => {
    const response = await api.post('/admin/database/maintenance/cleanup', {
      retentionDays,
    });
    return response.data?.data;
  },
  optimize: async (): Promise<{ optimized: boolean; executedAt: string; output: string | null }> => {
    const response = await api.post('/admin/database/maintenance/optimize');
    return response.data?.data;
  },
};

export type WorklistSettings = {
  retentionUndownloadedHours: number;
  retentionDownloadedHours: number;
  maxUploadSizeMb: number;
};

export type WorkProgramFile = {
  id: string;
  machineId: string;
  companyId?: string | null;
  originalFileName: string;
  fileSize: number;
  sha256: string;
  status: 'ACTIVE' | 'EXPIRED' | 'DELETED';
  firstDownloadedAt?: string | null;
  lastDownloadedAt?: string | null;
  deletedAt?: string | null;
  deleteReason?: string | null;
  createdAt: string;
  updatedAt?: string;
  pgmSheets?: number | null;
  pgmParts?: number | null;
  pgmMaterial?: number | null;
  pgmThickness?: number | null;
  pgmLength?: number | null;
  pgmWidth?: number | null;
};

export type WorkListItem = {
  id: string;
  orderIndex: number;
  quantity: number;
  quantityCompleted: number;
  quantityFailed: number;
  status: 'QUEUED' | 'SENT' | 'DOWNLOADED' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  addedInVersion: number;
  error?: string | null;
  sentAt?: string | null;
  downloadedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  failedAt?: string | null;
  cancelledAt?: string | null;
  hmiResult?: any;
  programFile: WorkProgramFile;
};

export type WorkList = {
  id: string;
  code: string;
  name: string;
  notes?: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  version: number;
  hmiAcknowledgedVersion: number;
  lastModifiedAt?: string | null;
  machineId: string;
  machine?: { id: string; name: string; serialNumber?: string };
  publishedAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  failedAt?: string | null;
  hmiLastPullAt?: string | null;
  hmiLastAckAt?: string | null;
  createdAt: string;
  updatedAt: string;
  items: WorkListItem[];
};

export const worklistService = {
  getSettings: async (): Promise<WorklistSettings> => {
    const response = await api.get('/worklists/settings');
    return response.data?.data || {
      retentionUndownloadedHours: 72,
      retentionDownloadedHours: 240,
      maxUploadSizeMb: 80,
    };
  },

  getFiles: async (machineId: string): Promise<WorkProgramFile[]> => {
    const response = await api.get(`/worklists/files?machineId=${encodeURIComponent(machineId)}&limit=200`);
    return response.data?.data?.files || [];
  },

  uploadFile: async (machineId: string, file: Blob & { name?: string }): Promise<WorkProgramFile> => {
    const safeName = (file as any)?.name || 'program.pgm';
    const response = await api.post(`/worklists/files?machineId=${encodeURIComponent(machineId)}`, file, {
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-File-Name': safeName,
      },
      timeout: 120000,
    });
    return response.data?.data?.file;
  },

  deleteFile: async (fileId: string): Promise<void> => {
    await api.delete(`/worklists/files/${fileId}`);
  },

  getWorkLists: async (machineId: string): Promise<WorkList[]> => {
    const response = await api.get(`/worklists?machineId=${encodeURIComponent(machineId)}&limit=200`);
    return response.data?.data?.workLists || [];
  },

  createWorkList: async (payload: {
    machineId: string;
    name: string;
    notes?: string;
    items: Array<{ programFileId: string; quantity: number; orderIndex: number }>;
  }): Promise<WorkList> => {
    const response = await api.post('/worklists', payload);
    return response.data?.data?.workList;
  },

  updateWorkList: async (
    id: string,
    payload: {
      name?: string;
      notes?: string;
      items?: Array<{ programFileId: string; quantity: number; orderIndex: number }>;
    }
  ): Promise<WorkList> => {
    const response = await api.put(`/worklists/${id}`, payload);
    return response.data?.data?.workList;
  },

  publishWorkList: async (id: string): Promise<WorkList> => {
    const response = await api.post(`/worklists/${id}/publish`);
    return response.data?.data?.workList;
  },

  cancelWorkList: async (id: string): Promise<WorkList> => {
    const response = await api.post(`/worklists/${id}/cancel`);
    return response.data?.data?.workList;
  },
};

export type ReportsMachinesResponse = {
  range: { start: string; end: string };
  summary: {
    productionSecondsTotal: number;
    productionHoursTotal: number;
    onlineSecondsTotal?: number;
    onlineHoursTotal?: number;
    idleOnSecondsTotal?: number;
    idleOnHoursTotal?: number;
    offlineSecondsTotal?: number;
    offlineHoursTotal?: number;
    averageUtilizationPercent: number;
    averageOnlinePercent?: number;
    jobsStartedTotal: number;
    jobsCompletedTotal: number;
    jobsFailedTotal: number;
    jobsCancelledTotal: number;
  };
  machines: Array<{
    machineId: string;
    machineName: string;
    machineModel: string;
    machineStatus: string;
    lastHeartbeat: string | null;
    productionSeconds: number;
    onlineSeconds?: number;
    idleOnSeconds?: number;
    offlineSeconds?: number;
    utilizationPercent: number;
    onlinePercent?: number;
    jobsStarted: number;
    jobsCompleted: number;
    jobsFailed: number;
    jobsCancelled: number;
    totalParts: number;
    partsCompleted: number;
  }>;
  byDay: Array<{
    day: string;
    jobsStarted: number;
    jobsCompleted: number;
    jobsFailed: number;
    jobsCancelled: number;
    productionMinutes: number;
    avgPowerKW: number;
    avgGasM3: number;
  }>;
  materials: Array<{ name: string; value: number }>;
  alarmsSummary: Array<{
    title: string;
    count: number;
    severity: string;
    avgDurationMinutes: number;
  }>;
};

export type MachineMaterialMapping = {
  id: string;
  machineId: string;
  sourceMaterial: string;
  mappedMaterial: string;
  createdAt: string;
  updatedAt: string;
};

export const reportsService = {
  getMachinesReport: async (params: {
    machineId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ReportsMachinesResponse> => {
    const response = await api.get('/reports/machines', { params });
    return response.data?.data;
  },

  getMaterialMappings: async (machineId: string): Promise<MachineMaterialMapping[]> => {
    const response = await api.get('/reports/material-mappings', {
      params: { machineId },
    });
    return response.data?.data?.mappings || [];
  },

  upsertMaterialMapping: async (payload: {
    machineId: string;
    sourceMaterial: string;
    mappedMaterial: string;
  }): Promise<MachineMaterialMapping> => {
    const response = await api.post('/reports/material-mappings', payload);
    return response.data?.data?.mapping;
  },

  deleteMaterialMapping: async (id: string): Promise<void> => {
    await api.delete(`/reports/material-mappings/${id}`);
  },
};

export const userService = {
  normalizeUserRole: (role?: string): User['role'] => {
    const normalized = (role || '').toLowerCase();
    if (normalized === 'customer') return 'client';
    if (normalized === 'administrator' || normalized === 'reseller' || normalized === 'client' || normalized === 'operator' || normalized === 'viewer') {
      return normalized;
    }
    return 'viewer';
  },

  getUsers: async (role?: string): Promise<User[]> => {
    const url = role ? `/users?role=${role}` : '/users';
    const response = await api.get(url);
    const users = response.data?.data?.users || [];

    // Map backend user to frontend User type
    return users.map((u: any) => ({
      ...u,
      name: u.displayName || u.name,
      role: userService.normalizeUserRole(u.roles?.[0]?.role?.name), // Flatten roles
      companyId: u.companyId || '',
    }));
  },

  createUser: async (userData: any): Promise<User> => {
    const response = await api.post('/users', userData);
    const u = response.data?.data?.user;

    return {
      ...u,
      name: u.displayName || u.name,
      role: userService.normalizeUserRole(u.roles?.[0]?.role?.name),
      companyId: u.companyId || '',
    };
  },

  updateUser: async (id: string, data: any): Promise<User> => {
    const response = await api.put(`/users/${id}`, data);
    const u = response.data?.data?.user;
    return {
      ...u,
      name: u.displayName || u.name,
      role: userService.normalizeUserRole(u.roles?.[0]?.role?.name),
      companyId: u.companyId || '',
    };
  },

  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
};

export const messageService = {
  getMessages: async (machineId: string): Promise<HMIMessage[]> => {
    const response = await api.get(`/messages?machineId=${machineId}`);
    return response.data?.data?.messages || [];
  },

  sendMessage: async (machineId: string, message: string, type: HMIMessage['type'] = 'info'): Promise<HMIMessage> => {
    // Keep both keys for compatibility with older/newer backend handlers.
    const response = await api.post('/messages', { machineId, message, type, messageType: type });
    return response.data?.data?.message;
  },

  markAsRead: async (messageId: string): Promise<void> => {
    await api.put(`/messages/${messageId}/read`);
  },

  getUnreadCounts: async (): Promise<Record<string, number>> => {
    const response = await api.get('/messages/unread-counts');
    return response.data?.data?.counts || {};
  },
};

export const dataService = {
  getMachineData: async (machineId: string, startDate?: Date, endDate?: Date): Promise<MachineData[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate.toISOString());
    if (endDate) params.append('endDate', endDate.toISOString());

    const response = await api.get(`/data/${machineId}?${params}`);
    return response.data;
  },

  getRealTimeData: async (machineId: string): Promise<MachineData> => {
    const response = await api.get(`/data/${machineId}/realtime`);
    return response.data;
  },
};

export const fileService = {
  uploadFile: async (file: File, companyId: string): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file as unknown as Blob);
    formData.append('companyId', companyId);

    const response = await api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  getFiles: async (companyId: string): Promise<File[]> => {
    const response = await api.get(`/files?companyId=${companyId}`);
    return response.data;
  },

  deleteFile: async (id: string): Promise<void> => {
    await api.delete(`/files/${id}`);
  },
};

export { productionService } from './productionService';

export default api;
